'use client';

import React, { useState } from 'react';

import { usePathname } from 'next/navigation';

import Container from '@repo/ui/container';
import Icon from '@repo/ui/icon';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from '@repo/ui/navigation-menu';
import { Sheet, SheetContent, SheetTrigger } from '@repo/ui/sheet';

import { toClassName } from 'helpers/format';

import { GeneralSettings } from 'types/sanity.types';

import { useTranslation } from 'hooks/use-translation';

import Link from 'components/nextjs-fixes/link';

import LogoSection from './_components/logo-section';

import { styles } from '.';

type TProps = {
  locale: string;

  general?: GeneralSettings;

  routes: {
    id: string;
    label: string;
    url: string;
    subroutes?: { id: string; label: string; url: string }[];
  }[];
};

const NavBar = (props: TProps) => {
  const { locale, general, routes } = props;

  const [isOpen, setIsOpen] = useState(false);

  const { t } = useTranslation(locale);

  const pathname = usePathname();

  return (
    <div className={styles.container}>
      <Container>
        <div className={styles.content}>
          <LogoSection logoUrl={general?.logo.asset?.url} locale={locale} />

          <div className="w-full hidden xl:block">
            <NavigationMenu viewport={false}>
              <NavigationMenuList>
                {routes.map((route) => {
                  if (
                    route.subroutes === undefined ||
                    route.subroutes?.length === 0
                  ) {
                    return (
                      <NavigationMenuItem key={route.id}>
                        <NavigationMenuLink
                          asChild
                          className={toClassName(
                            styles.link,
                            pathname.includes(route.url) && ' !text-red-500',
                          )}
                        >
                          <Link href={route.url}>{route.label}</Link>
                        </NavigationMenuLink>
                      </NavigationMenuItem>
                    );
                  }

                  return (
                    <NavigationMenuItem key={route.id}>
                      <NavigationMenuTrigger
                        className={toClassName(
                          styles.link,
                          pathname.includes(route.url) &&
                            '!font-semibold !text-red-500',
                        )}
                      >
                        {route.label}
                      </NavigationMenuTrigger>
                      <NavigationMenuContent>
                        <ul className="grid w-[300px] gap-4">
                          <li>
                            {route.subroutes?.map((subroute) => (
                              <NavigationMenuLink
                                key={subroute.id}
                                asChild
                                className={toClassName(
                                  styles.link,

                                  pathname.includes(subroute.url) &&
                                    '!font-semibold',
                                )}
                              >
                                <Link href={subroute.url}>
                                  {subroute.label}
                                </Link>
                              </NavigationMenuLink>
                            ))}
                          </li>
                        </ul>
                      </NavigationMenuContent>
                    </NavigationMenuItem>
                  );
                })}
              </NavigationMenuList>
            </NavigationMenu>
          </div>

          <div className="flex gap-x-8">
            <div className="hidden md:flex min-w-xs items-end justify-end">
              <Link
                href={`/${locale}/productions`}
                className="text-base px-6 border-3 border-black py-3 font-bold text-black hover:border-red-500 hover:text-red-500 duration-300"
              >
                {t('component.navbar.label.book-a-production')}
              </Link>
            </div>

            <Sheet open={isOpen} onOpenChange={(value) => setIsOpen(value)}>
              <SheetTrigger className="block lg:hidden">
                <Icon
                  type="sharp"
                  accessor="bars"
                  className="text-black text-3xl px-4 cursor-pointer hover:text-black/90"
                />
              </SheetTrigger>
              <SheetContent className="!p-0 !m-0 border-6-l border-black flex flex-col items-start justify-start">
                <div className="py-8 px-6 grow flex flex-col gap-y-6">
                  <div onClick={() => setIsOpen(false)}>
                    <LogoSection
                      logoUrl={general?.logo.asset?.url}
                      locale={locale}
                    />
                  </div>

                  <NavigationMenu
                    viewport={false}
                    className="grow flex flex-col items-start justify-start"
                  >
                    <NavigationMenuList className="flex !flex-col !items-start !justify-start !text-left ">
                      {routes.map((route) => {
                        if (
                          route.subroutes === undefined ||
                          route.subroutes?.length === 0
                        ) {
                          return (
                            <NavigationMenuItem key={route.id}>
                              <NavigationMenuLink
                                asChild
                                className={toClassName(
                                  styles.link,
                                  pathname === route.url &&
                                    '!font-semibold !text-red-500',
                                )}
                                onClick={() => setIsOpen(false)}
                              >
                                <Link href={route.url}>{route.label}</Link>
                              </NavigationMenuLink>
                            </NavigationMenuItem>
                          );
                        }

                        return (
                          <NavigationMenuItem key={route.id}>
                            <NavigationMenuTrigger
                              className={toClassName(
                                styles.link,
                                pathname.includes(route.url) &&
                                  '!font-semibold !text-red-500',
                              )}
                            >
                              {route.label}
                            </NavigationMenuTrigger>
                            <NavigationMenuContent>
                              <ul className="grid w-[300px] gap-4">
                                <li>
                                  {route.subroutes?.map((subroute) => (
                                    <NavigationMenuLink
                                      key={subroute.id}
                                      asChild
                                      className={toClassName(
                                        styles.link,

                                        pathname.includes(subroute.url) &&
                                          '!font-semibold',
                                      )}
                                      onClick={() => setIsOpen(false)}
                                    >
                                      <Link href={subroute.url}>
                                        {subroute.label}
                                      </Link>
                                    </NavigationMenuLink>
                                  ))}
                                </li>
                              </ul>
                            </NavigationMenuContent>
                          </NavigationMenuItem>
                        );
                      })}
                    </NavigationMenuList>
                  </NavigationMenu>

                  <div onClick={() => setIsOpen(false)}>
                    <Link
                      href={`/${locale}/productions`}
                      className="uppercase text-lg px-6 border-3 border-black py-3 font-bold text-black hover:border-red-500 hover:text-red-500 duration-300"
                    >
                      {t('component.navbar.label.book-a-production')}
                    </Link>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </Container>
    </div>
  );
};

export default NavBar;
